import React, { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from "react";
import { prepareDefaultValues, prepareInitialConfig } from "../../../../utils/formHelper";
import usePageState from "../../../../utils/customHooks/usePageState";
import { useParams } from "react-router-dom";
import ProSelectField from "../../../Components/Inputs/SelectField";
import ProTextInput from "../../../Components/Inputs/TextField";
import EmailField from "../../../Components/Inputs/EmailField";
import MobileField from "../../../Components/Inputs/MobileField";
import DeleteCell from "../../../Components/ProGrid/components/DeleteCell";
import NameCell from "../../../Components/ProGrid/components/NameCell";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import PropTypes from "prop-types";
import FormGridInsideSectionLayout from "../../../Components/ProForms/ProFormsLayout/FormGridInsideSectionLayout.jsx";
import {
  ADD_PROJECT_CONTACT_INFO,
  DELETE_PROJECT_CONTACT_INFO,
  DYNAMIC_DROPDOWN,
  GET_ALL_PROJECT_CONTACT_INFO,
  UPDATE_PROJECT_CONTACT_INFO
} from "../../../../utils/services/apiPath";
import { deleteApi, postApi } from "../../../../utils/services";
import { STAUTES } from "../../../../utils/constant";
import ProGrid from "../../../Components/ProGrid/v2";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import usePermission from "../../../../utils/customHooks/usePermission";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { R_PROJECTS, R_PROJECTS_INFO } from "../../../../utils/permissionReferenceConfig";

const projectContactFormConfing = {
  contactTypeId: {
    label: "Contact Type",
    // perKey: "contactType",
    rules: { required: "Required!" }
  },
  contactFirstName: {
    label: "First Name",
    // perKey: "FirstName",
    rules: { required: "Required!" }
  },
  contactLastName: {
    label: "Last Name",
    // perKey: "LastName",
    rules: { required: "Required!" }
  },
  contactEmail: {
    label: "Email",
    // perKey: "ContactEmail",
    rules: { required: "Required!" }
  },
  contactPhone: {
    label: "Contact Phone",
    // perKey: "ContactPhone",
    rules: {
      required: "Required!"
    }
  },
  contactTitle: {
    label: "Contact Title",
    // perKey: "ContactTitle",
    rules: { required: "Required!" }
  }
};

const ProjectContactInfo = forwardRef(({ globalEditMode, editSource, onContactCancel, onContactEdit }, ref) => {
  let { id } = useParams();
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(projectContactFormConfing));
  const [contactTypesData, setContactTypesData] = useState([]);
  const [actionData, setActionData] = useState({});
  const [projectContactInfoData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const {
    currentPage,
    pageSize,
    data: contactsData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
  } = usePageState();

  const {
    "Project Contacts": projectContactsPermission
  } = usePermission(
    subModPermissionSelector(R_PROJECTS, R_PROJECTS_INFO),
    ["Project Contacts"]
  );

  const onDelete = async projectInfoContactsId => {
    const { error } = await deleteApi(`${DELETE_PROJECT_CONTACT_INFO}?projectId=${id}`, [{ projectInfoContactsId }]);
    if (error) return errorToast(error);
    getAllProjectContacts();
    successToast("Project Contact deleted successfully.");
  };

  useEffect(() => {
    if (globalEditMode && editSource === "contact") {
      onEdit();
    }

    if (!globalEditMode) {
      resetForm();
    }
  }, [globalEditMode, editSource]);

  const columns = useMemo(() => [
    {
      field: "contactFullName",
      headerName: "Full Name",
      renderCell: params => <NameCell params={params} onClick={() => handleEditRow(params.row)} />
    },
    {
      field: "contactTitle",
      headerName: "Labor Classification Title"
    },
    {
      field: "contactType",
      headerName: "Contact Type"
    },
    {
      field: "contactPhone",
      headerName: "Contact Phone"
    },
    {
      field: "contactEmail",
      headerName: "Contact Email"
    },
    {
      field: "projectInfoContactsId",
      headerName: "Actions",
      isDeleteColumn: true,
      disableExport: true,
      width: 80,
      renderCell: params => (
        <DeleteCell 
          title={"Delete Project Info Contact"} 
          params={params}
          onDelete={onDelete}
          disabled={!globalEditMode}
        />
      )
    }
  ], [globalEditMode]);

  const { control, setValue, trigger, getValues, clearErrors } = useForm({ defaultValues: prepareDefaultValues(projectContactFormConfing) });


  useImperativeHandle(ref, () => ({
  validateContactForm: async () => {
    const values = getValues();

    const hasStarted = Object.values(values).some( val => val !== null && val !== undefined && val !== "");

    if (hasStarted) {
      return await trigger();
    }

    return true;
  },

  saveContactFromParent: async () => {
    const values = getValues();
    const hasStarted = Object.values(values).some( val => val !== null && val !== undefined && val !== "");
    if (!hasStarted) return true;
    const isValid = await trigger();
    if (!isValid) return false;
    const payload = {
      projectId: id,
      contactTypeId: values.contactTypeId,
      contactFirstName: values.contactFirstName,
      contactLastName: values.contactLastName,
      contactEmail: values.contactEmail,
      contactPhone: values.contactPhone,
      contactTitle: values.contactTitle,
      projectInfoContactsId: actionData?.projectInfoContactsId || null
    };

    const { error } = await postApi(actionData?.projectInfoContactsId ? UPDATE_PROJECT_CONTACT_INFO : ADD_PROJECT_CONTACT_INFO, payload);

    if (error) {
      errorToast(error);
      return false;
    }

    getAllProjectContacts();
    resetForm();
    return true;
  }
}));



  const handleReadValueClick = name => {
    onEdit();
    onContactEdit?.();
  };

  const defaultFormProps = { control, formValues: projectContactInfoData, handleReadValueClick };

  const handleEditRow = async data => {
    setActionData(data);
    if (data) {
      setValue("contactTypeId", data.contactTypeId);
      setValue("contactFirstName", data.contactFirstName);
      setValue("contactLastName", data.contactLastName);
      setValue("contactEmail", data.contactEmail);
      setValue("contactPhone", data.contactPhone);
      setValue("contactTitle", data.contactTitle);
    }
    onEdit();
  };

  const onEdit = () => {
    setFormConfig(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: { ...prev[key], mode: "edit" } }), {}));
  };

  const resetForm = () => {
    setActionData({});
    setFormConfig(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: { ...prev[key], mode: "read" } }), {}));
    Object.keys(formConfig).forEach(field => setValue(field, null));
    clearErrors();
  };

  const cancelAddUpdateRow = () => {
    resetForm();
    clearErrors();
    onContactCancel?.();
  };

  const getAllProjectContacts = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const payload = {
      pageIndex: searchString ? 1 : currentPage + 1,
      pageSize: pageSize,
      searchString: searchString,
      orderByAscending: true,
      orderCol: "",
      ...sortModel
    };
    const { data, totalRecords, error } = await postApi(GET_ALL_PROJECT_CONTACT_INFO, payload, { params: { projectId: id } });
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  const getAllContactType = async () => {
    const payload = ["contactType"];
    const data = await postApi(DYNAMIC_DROPDOWN, payload);
    setContactTypesData(data[0]?.typeDropDowns?.map(item => ({ label: item.label, value: item.intValue })));
  };

  useEffect(() => {
    getAllProjectContacts();
    getAllContactType();
  }, [getAllProjectContacts]);

  const fieldPermissions = {
    canView: projectContactsPermission.canView,
    canAdd: projectContactsPermission.canAdd,
    canEdit: projectContactsPermission.canEdit
  };

  return (
    <div className="proFormGridInsideSectionLayout-wrapper">
      <FormGridInsideSectionLayout
        showGrid={true}
        beingEdited={false}
        hideEdit={true}
        isSaving={isSaving}
      >
        <Grid container spacing={2}>
          <ProSelectField {...defaultFormProps} {...formConfig.contactTypeId} options={contactTypesData} permission={fieldPermissions} />
          <ProTextInput {...defaultFormProps} {...formConfig.contactFirstName} permission={fieldPermissions} />
          <ProTextInput {...defaultFormProps} {...formConfig.contactLastName} permission={fieldPermissions} />
          <EmailField {...defaultFormProps} {...formConfig.contactEmail} permission={fieldPermissions} />
          <MobileField {...defaultFormProps} {...formConfig.contactPhone} permission={fieldPermissions} />
          <ProTextInput size={4} {...defaultFormProps} {...formConfig.contactTitle} permission={fieldPermissions} />
        </Grid>
      </FormGridInsideSectionLayout>
      <ProGrid
        loading={status === STAUTES.LOADING}
        options={{
          getRowId: row => row.projectInfoContactsId,
          rowCount: rowCount,
          paginationMode: "server",
          paginationModel: { pageSize: pageSize, page: currentPage },
          onPaginationModelChange: setPagination,
        }}
        hideIfNoData={true}
        hideSearch={true}
        columns={columns}
        hideExport
        hideAddButton={true}
        rows={contactsData || []}
      />
    </div>
  );
});


ProjectContactInfo.propTypes = {
  globalEditMode: PropTypes.bool,
  editSource: PropTypes.string,
  onContactCancel: PropTypes.func,
  onContactEdit: PropTypes.func
};

export default ProjectContactInfo;
